import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './redux/store';
import Layout from './pages/layout';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { PersistGate } from 'redux-persist/integration/react';
import { persistor } from './redux/store';
import ProtectedRoute from './components/common/protected-route';
import AdminProtectedRoute from './components/common/admin-protected-route';
import { CircularProgress, Box } from '@mui/material';

// Anında yüklenen sayfalar (kritik)
import HomePage from './pages/homepage.js';

// Lazy loaded sayfalar
const SearchResults = React.lazy(() => import('./pages/search-results.js'));
const Coach = React.lazy(() => import('./pages/coach.js'));
const SignUp = React.lazy(() => import('./pages/SignUp/signup.js'));
const SignUpStepTwo = React.lazy(() => import('./pages/SignUp/signup-step-two.js'));
const SignUpStepThree = React.lazy(() => import('./pages/SignUp/signup-step-three.js'));
const SignUpStepFour = React.lazy(() => import('./pages/SignUp/signup-step-four.js'));
const SignUpStepFive = React.lazy(() => import('./pages/SignUp/signup-step-five.js'));
const SignUpStepSix = React.lazy(() => import('./pages/SignUp/signup-step-six.js'));
const SignUpSummary = React.lazy(() => import('./pages/SignUp/signup-summary'));
const SignIn = React.lazy(() => import('./pages/SignIn/signin'));
const Profile = React.lazy(() => import('./pages/Profile/profile'));
const ProfileEdit = React.lazy(() => import('./pages/Profile/profile-edit'));
const CoachingDetail = React.lazy(() => import('./pages/Profile/coaching-detail'));
const CoachingDetailEdit = React.lazy(() => import('./pages/Profile/coaching-detail-edit'));
const Certificates = React.lazy(() => import('./pages/Profile/certificates'));
const CategoryList = React.lazy(() => import('./pages/category-list.js'));
const CategoryDetail = React.lazy(() => import('./pages/category-detail.js'));
const PrivacyPolicy = React.lazy(() => import('./pages/privacy-policy.js'));
const TermsAndConditions = React.lazy(() => import('./pages/terms-and-conditions.js'));
const ForgetPassword = React.lazy(() => import('./pages/forget-password'));
const ResetPassword = React.lazy(() => import('./pages/reset-password'));
const VerifyAccount = React.lazy(() => import('./pages/verify-account'));
const FacebookDataDeletion = React.lazy(() => import('./pages/facebook-data-deletion'));
const DeleteAccount = React.lazy(() => import('./pages/delete-account'));
const LinkedInAIChat = React.lazy(() => import('./pages/linkedin-ai-chat'));
const AdminDashboard = React.lazy(() => import('./pages/admin/dashboard'));
const AdminUsers = React.lazy(() => import('./pages/admin/users'));
const AdminCategories = React.lazy(() => import('./pages/admin/categories'));
const AdminUserEdit = React.lazy(() => import('./pages/admin/user-edit'));

import TimeoutModal from './components/common/timeout-modal';
import { useIdleTimeout } from './hooks/useIdleTimeout';
import { useSitemap } from './hooks/useSitemap';
import { useAnalytics } from './hooks/useAnalytics';
import PerformanceHelper from './helpers/performance-helper';

dayjs.locale('tr');

// Loading componenti
const LoadingSpinner = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '50vh',
    }}
  >
    <CircularProgress size={40} sx={{ color: 'primary.main' }} />
  </Box>
);

const AppRoutes = () => {
  const { showWarning, onExtendSession, onCloseWarning, countdown } = useIdleTimeout();

  // Initialize sitemap generation (only works in production)
  useSitemap();

  // Initialize analytics tracking
  useAnalytics();

  // Initialize performance optimizations (only works in production)
  React.useEffect(() => {
    PerformanceHelper.initializePerformanceOptimizations();
  }, []);

  return (
    <>
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/linkedin-ai-chat" element={<LinkedInAIChat />} />

          <Route path="/" element={<Layout />}>
            <Route index element={<HomePage />} />
            <Route path="/search-results" element={<SearchResults />} />
            <Route path="/coach/:slug" element={<Coach />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/forget-password" element={<ForgetPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-account" element={<VerifyAccount />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/edit"
              element={
                <ProtectedRoute>
                  <ProfileEdit />
                </ProtectedRoute>
              }
            />
            <Route path="/signup-step-two" element={<SignUpStepTwo />} />
            <Route path="/signup-step-three" element={<SignUpStepThree />} />
            <Route path="/signup-step-four" element={<SignUpStepFour />} />
            <Route path="/signup-step-five" element={<SignUpStepFive />} />
            <Route path="/signup-step-six" element={<SignUpStepSix />} />
            <Route path="/signup-summary" element={<SignUpSummary />} />
            <Route
              path="/coach-profile"
              element={
                <ProtectedRoute>
                  <CoachingDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach-profile/edit"
              element={
                <ProtectedRoute>
                  <CoachingDetailEdit />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coach-profile/certificates"
              element={
                <ProtectedRoute>
                  <Certificates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <AdminProtectedRoute>
                  <AdminDashboard />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <AdminProtectedRoute>
                  <AdminUsers />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <AdminProtectedRoute>
                  <AdminCategories />
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/user-edit/:userId"
              element={
                <AdminProtectedRoute>
                  <AdminUserEdit />
                </AdminProtectedRoute>
              }
            />

            {/* İngilizce kategori rotaları */}
            <Route path="categories" element={<CategoryList />} key="en-categories" />
            <Route path="category/:categorySlug" element={<CategoryDetail />} key="en-category-detail" />
            <Route path="en/categories" element={<CategoryList />} key="en-categories-prefix" />
            <Route path="en/category/:categorySlug" element={<CategoryDetail />} key="en-category-detail-prefix" />

            {/* Türkçe kategori rotaları */}
            <Route path="kategoriler" element={<CategoryList />} key="tr-categories" />
            <Route path="kategori/:categorySlug" element={<CategoryDetail />} key="tr-category-detail" />
            <Route path="tr/kategoriler" element={<CategoryList />} key="tr-categories-prefix" />
            <Route path="tr/kategori/:categorySlug" element={<CategoryDetail />} key="tr-category-detail-prefix" />

            {/* Kullanım Şartları rotaları */}
            <Route path="kullanim-sartlari" element={<TermsAndConditions />} key="tr-terms" />
            <Route path="terms-and-conditions" element={<TermsAndConditions />} key="en-terms" />
            <Route path="tr/kullanim-sartlari" element={<TermsAndConditions />} key="tr-terms-prefix" />
            <Route path="en/terms-and-conditions" element={<TermsAndConditions />} key="en-terms-prefix" />

            {/* Gizlilik Politikası rotaları */}
            <Route path="gizlilik-politikasi" element={<PrivacyPolicy />} key="tr-privacy" />
            <Route path="privacy-policy" element={<PrivacyPolicy />} key="en-privacy" />
            <Route path="tr/gizlilik-politikasi" element={<PrivacyPolicy />} key="tr-privacy-prefix" />
            <Route path="en/privacy-policy" element={<PrivacyPolicy />} key="en-privacy-prefix" />

            {/* Facebook Data Deletion rotaları */}
            <Route path="facebook-data-deletion" element={<FacebookDataDeletion />} key="en-facebook-data" />
            <Route path="facebook-veri-silme" element={<FacebookDataDeletion />} key="tr-facebook-data" />
            <Route path="en/facebook-data-deletion" element={<FacebookDataDeletion />} key="en-facebook-data-prefix" />
            <Route path="tr/facebook-veri-silme" element={<FacebookDataDeletion />} key="tr-facebook-data-prefix" />
          </Route>
        </Routes>
      </Suspense>

      <TimeoutModal open={showWarning} onClose={onCloseWarning} onExtend={onExtendSession} countdown={countdown} />
    </>
  );
};

const App = () => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Router>
            <AppRoutes />
          </Router>
        </LocalizationProvider>
      </PersistGate>
    </Provider>
  );
};

export default App;
